import { Component, EventEmitter, Input , Output, SimpleChanges  } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import DOMPurify from 'dompurify';
import { VideoThumbnailService } from '../services/video-thumbnail.service';
import { environment } from '../../../environments/environment';
import { firstValueFrom } from 'rxjs';
import { SharedService } from '../../shared/services/shared.service';

@Component({
  selector: 'app-video-card',
  templateUrl: './video-card.component.html',
  styleUrls: ['./video-card.component.css'],
  standalone: false
})
export class VideoCardComponent {
  @Input() video: any;
  @Input() isHomePage ?: string; // Flag to check if it's the home page
  @Input() index!: number;
  @Output() mediaReady = new EventEmitter<void>();
  hovered = false;

  private generatedThumbnailObjectUrl?: string;
  private resolvedThumbnailObjectUrl?: string;
  private resolvedVttObjectUrl?: string;
  private resolvedVideoObjectUrl?: string;
  private nativePlayerWired = false;
  private hasEmittedMediaReady = false;

  private readonly postObjectUrlCache = new Map<string, string>();
  private readonly postObjectUrlInFlight = new Map<string, Promise<string>>();

  // 1x1 transparent gif so <img src> never triggers a network request when we intentionally blank it.
  private readonly transparentPixel =
    'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///ywAAAAAAQABAAACAUwAOw==';

  constructor(
    private readonly videoThumbnailService: VideoThumbnailService,
    private readonly http: HttpClient,
    private readonly sharedService: SharedService
  ) {}

  ngOnInit(): void {
     // Normalize API values (some endpoints send is_playing as "TRUE"/"FALSE" strings).
    this.coerceVideoFlags(this.video);

  if (this.video?.vtt_text) {
    // Sanitize VTT text before creating blob
    const sanitizedVtt = DOMPurify.sanitize(this.video.vtt_text);
    this.video.vttBlobUrl = this.generateVttBlobUrl(sanitizedVtt);
  }

    // NOTE: Do not call ensureVttBlobUrl()/ensureThumbnail() here.
    // Angular calls ngOnChanges() before ngOnInit() on first render; keeping the work in ngOnChanges
    // prevents double-fetching media.
}

 ngOnChanges(changes: SimpleChanges): void {
    if (changes['video']) {
      this.hasEmittedMediaReady = false;
      this.coerceVideoFlags(this.video);
      this.prepareProtectedMediaPlaceholders(this.video);
      this.ensureVttBlobUrl();
      this.ensureThumbnail();

      if (!this.video || (this.isIframeVideo() && this.isEmptyish(this.video?.videoUrl))) {
        this.emitMediaReady();
      }
    }
  }

   ngOnDestroy(): void {
    this.revokeGeneratedThumbnailObjectUrl();
    this.revokeResolvedObjectUrls();
  }
    // Returns the static thumbnail image URL if available
    getThumbnailSrc(video: any): string {
      const src = String(video?.thumbnailUrl || '').trim();
      if (!src) return this.transparentPixel;
      return src;
    }

  private prepareProtectedMediaPlaceholders(video: any): void {
    if (!video) return;

    // If backend provides thumbnailPost, prefer it over URL-based thumbnails.
    // We intentionally blank thumbnailUrl to avoid the browser issuing an unauthenticated GET.
    if (video?.thumbnailPost && typeof video.thumbnailPost === 'object') {
      (video as any).__thumbnailPost = video.thumbnailPost;
      video.thumbnailUrl = '';
    }

    // Same idea for VTT: store the URL so we can resolve to blob: on demand.
    const rawVtt = String(video.vttUrl || '').trim();
    if (rawVtt && !/^(blob:|data:)/i.test(rawVtt)) {
      const normalizedVtt = this.buildMediaUrl(rawVtt);
      if (this.isApiUrl(normalizedVtt)) {
        (video as any).__vttApiUrl = normalizedVtt;
      }
    }
  }

  private async ensureThumbnail(): Promise<void> {
    const video = this.video;
    if (!video) return;
    if (this.isIframeVideo()) return;

    // 1) Preferred: resolve via thumbnailPost (POST + render as image)
    const thumbPost = (video as any).__thumbnailPost ?? video?.thumbnailPost;
    if (thumbPost && typeof thumbPost === 'object') {
      const method = String(thumbPost?.method || '').toUpperCase();
      const url = String(thumbPost?.url || '').trim();
      if (method === 'POST' && url) {
        const resolved = await this.resolveThumbnailObjectUrlFromPost({ url, body: thumbPost?.body });
        if (resolved) {
          video.thumbnailUrl = resolved;
          this.emitMediaReady();
          return;
        }

        // Emit readiness once thumbnail POST responds, even if fallback generation continues.
        this.emitMediaReady();
      }
    }

    if (!this.isEmptyish(video.thumbnailUrl)) {
      return;
    }

    const effectiveVideoUrl = this.getEffectiveVideoUrl(video);
    if (this.isEmptyish(effectiveVideoUrl)) {
      this.emitMediaReady();
      return;
    }

    // If we already uploaded in this session, use cached URL even if list API still returns empty.
    const cached = this.getCachedThumbnailUrl(video);
    if (this.isEmptyish(video.thumbnailUrl) && cached && !this.isEmptyish(cached)) {
      video.thumbnailUrl = cached;
      return;
    }

    // If this failed previously (e.g., due to CORS), avoid hot-looping but allow retry later.
    if (video.__thumbnailGenerationFailed) {
      const lastFail = Number(video.__thumbnailGenerationFailedAt || 0);
      const cooldownMs = 60_000;
      if (lastFail && Date.now() - lastFail < cooldownMs) {
        return;
      }
      // allow retry after cooldown
      video.__thumbnailGenerationFailed = false;
    }

    // guard against duplicate work if change detection runs repeatedly
    if (video.__thumbnailGenerationInProgress) return;
    video.__thumbnailGenerationInProgress = true;

    let tempVideoObjectUrl: string | undefined;
    try {
      // For /v4/sync/blob/video URLs, generate thumbnail from the direct URL so the browser can
      // stream via Range requests using cookies.
      const thumbnailSourceUrl = await this.getPlayableUrlForProtectedMedia(effectiveVideoUrl, { purpose: 'thumbnail' });
      if (/^blob:/i.test(thumbnailSourceUrl)) {
        tempVideoObjectUrl = thumbnailSourceUrl;
      }

      const generated = await this.videoThumbnailService.runLimited(() =>
        this.videoThumbnailService.generateFromVideoUrl(thumbnailSourceUrl, {
          seekToSeconds: 1,
          maxWidth: 1920,
          imageType: 'image/jpeg',
          quality: 0.98,
          preload: 'metadata',
          crossOrigin: 'anonymous'
        })
      );

     

      // Use the in-memory object URL immediately for display.
      video.thumbnailUrl = generated.objectUrl;
      this.setGeneratedThumbnailObjectUrl(generated.objectUrl);

      // Optional: upload to Azure Blob if backend provided a SAS PUT URL.
      const sasPutUrl: string = ""

      // Upload bytes to Node (static endpoint) when thumbnail doesn't exist.
      // Your backend route: syncRoutesv2.post('/blob/thumb/upload', upload.single('thumbnail'), ...)
      
        const uploadApiUrl: string = `${environment.apiUrl}/v4/sync/blob/thumb/upload`;

        // build a stable filename based on the video blobname (if present)
        const baseName = String(video.video_blobname || video.videoBlobName || video.id || this.index)
          .replace(/\.[^/.]+$/, '')
          .replace(/[^a-zA-Z0-9-_ ]/g, '')
          .trim()
          .replace(/\s+/g, '_');
        const filename = `${baseName || 'thumb'}.jpg`;

        const uploadVideoBlobName = String(video.video_blobname ?? video.videoBlobName ?? '');

      

        const thumbnailBase64 = await this.videoThumbnailService.blobToDataUrl(generated.blob);
       

        const result = await this.videoThumbnailService.runLimited(() =>
          this.videoThumbnailService.uploadToBackendJson(uploadApiUrl, {
            videoId: String(video.id ?? ''),
            videoBlobName: uploadVideoBlobName,
            contentType: generated.imageType,
            filename,
            thumbnailBase64
          })
        );

       

        // If backend returns a URL, switch to it.
        const readUrl = (result && (result.thumbnailUrl || result.url || result.readUrl)) as string | undefined;
        if (readUrl) {
          const normalized = this.buildMediaUrl(readUrl);
          video.thumbnailUrl = normalized;
          this.cacheThumbnailUrl(video, normalized);
          // Keep the generated preview until the real URL loads; do not revoke here.
        }
      
    } catch (e) {
      // Non-fatal: we can still play the video without a thumbnail.
      console.warn('Thumbnail generation/upload failed', e);
      video.__thumbnailGenerationFailed = true;
      video.__thumbnailGenerationFailedAt = Date.now();
      this.emitMediaReady();
    } finally {
      video.__thumbnailGenerationInProgress = false;
      if (tempVideoObjectUrl) {
        try {
          URL.revokeObjectURL(tempVideoObjectUrl);
        } catch {
          // ignore
        }
      }
    }
  }

  private stripTokenQueryParam(url: string): string {
    if (!url) return '';
    try {
      const parsed = new URL(url, environment.apiUrl);
      // Remove the legacy token-in-URL pattern.
      parsed.searchParams.delete('token');
      parsed.searchParams.delete('access_token');
      return parsed.toString();
    } catch {
      return url;
    }
  }

  private rewriteSyncBlobVideoPathToQuery(url: string): string {
    if (!url) return '';

    try {
      const parsed = new URL(url, environment.apiUrl);
      const apiBase = new URL(environment.apiUrl);
      if (parsed.origin !== apiBase.origin) return url;

      // Only rewrite the path-style video streaming endpoint.
      const marker = '/v4/sync/blob/video/';
      const queryEndpointPath = '/v4/sync/blob/video';

      const safeDecode = (value: string): string => {
        try {
          return decodeURIComponent(value);
        } catch {
          return value;
        }
      };

      const encodeForQueryKeepSlashes = (decoded: string): string => {
        return encodeURIComponent(decoded).replace(/%2F/gi, '/');
      };

      // Case 1: already query-style; just normalize blobName encoding for readability.
      if (parsed.pathname === queryEndpointPath && (parsed.searchParams.has('blobName') || parsed.searchParams.has('name'))) {
        const decodedBlobName = parsed.searchParams.get('blobName') ?? parsed.searchParams.get('name') ?? '';
        const encodedForQuery = encodeForQueryKeepSlashes(decodedBlobName);

        const pairs: string[] = [];
        parsed.searchParams.forEach((value, key) => {
          if (key === 'blobName' || key === 'name') return;
          pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        });
        pairs.push(`blobName=${encodedForQuery}`);

        const query = pairs.length ? `?${pairs.join('&')}` : '';
        return `${parsed.origin}${queryEndpointPath}${query}${parsed.hash || ''}`;
      }

      // Case 2: path-style; convert to query-style.
      if (!parsed.pathname.startsWith(marker)) return url;

      const nameFromPath = parsed.pathname.substring(marker.length);
      // Decode once, then encode for query param. Keep '/' readable.
      const decodedBlobName = safeDecode(nameFromPath);
      const encodedForQuery = encodeForQueryKeepSlashes(decodedBlobName);

      const endpointPath = marker.slice(0, -1); // remove trailing '/'

      const pairs: string[] = [];
      parsed.searchParams.forEach((value, key) => {
        // Keep any existing params except duplicates.
        if (key === 'blobName' || key === 'name') return;
        pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
      });
      pairs.push(`blobName=${encodedForQuery}`);

      const query = pairs.length ? `?${pairs.join('&')}` : '';
      return `${parsed.origin}${endpointPath}${query}${parsed.hash || ''}`;
    } catch {
      return url;
    }
  }

  private buildMediaUrl(url: string): string {
    if (!url) return '';

    const trimmedUrl = url.trim();
    if (trimmedUrl.length === 0) return '';

    const lower = trimmedUrl.toLowerCase();
    if (lower === 'null' || lower === 'undefined') return '';

    // data:, blob:, http(s): are already usable
    if (/^(data:|blob:|https?:\/\/)/i.test(trimmedUrl)) {
      return this.rewriteSyncBlobVideoPathToQuery(this.stripTokenQueryParam(trimmedUrl));
    }

    // Always normalize relative media paths to the API base.
    return this.rewriteSyncBlobVideoPathToQuery(
      this.stripTokenQueryParam(
        `${environment.apiUrl}${trimmedUrl.startsWith('/') ? trimmedUrl : `/${trimmedUrl}`}`
      )
    );
  }

  private isApiUrl(url: string): boolean {
    try {
      const base = new URL(environment.apiUrl);
      const resolved = new URL(url, environment.apiUrl);
      return resolved.origin === base.origin;
    } catch {
      return false;
    }
  }

  private async fetchBlobObjectUrl(url: string): Promise<string> {
    const blob = await firstValueFrom(this.http.get(url, { responseType: 'blob' }));
    return URL.createObjectURL(blob);
  }

  private stableStringify(value: any): string {
    try {
      return JSON.stringify(value ?? {});
    } catch {
      return '';
    }
  }

  private async resolveThumbnailObjectUrlFromPost(config: { url: string; body?: any }): Promise<string> {
    const normalizedUrl = this.buildMediaUrl(config.url);
    if (this.isEmptyish(normalizedUrl)) return '';

    const cacheKey = `THUMB POST ${normalizedUrl} ${this.stableStringify(config.body)}`;
    const cached = this.postObjectUrlCache.get(cacheKey);
    if (cached) return cached;

    const inFlight = this.postObjectUrlInFlight.get(cacheKey);
    if (inFlight) return inFlight;

    const promise = (async () => {
      try {
        const resolved = await this.sharedService.postImageAsRenderableUrl(normalizedUrl, config.body ?? {}, {
          withCredentials: false,
          includeAuthorization: false
        });
        return resolved?.url || '';
      } catch {
        return '';
      }
    })();

    this.postObjectUrlInFlight.set(cacheKey, promise);
    const resolvedUrl = await promise;
    this.postObjectUrlInFlight.delete(cacheKey);

    if (resolvedUrl) {
      this.postObjectUrlCache.set(cacheKey, resolvedUrl);
    }
    return resolvedUrl;
  }

  private async fetchText(url: string): Promise<string> {
    return firstValueFrom(this.http.get(url, { responseType: 'text' }));
  }

  private async getPlayableUrlForProtectedMedia(
    url: string,
    options: { purpose: 'video' | 'thumbnail' | 'thumbnail-image' | 'vtt' }
  ): Promise<string> {
    const normalized = this.buildMediaUrl(url);
    if (this.isEmptyish(normalized)) return '';
    if (/^(blob:|data:)/i.test(normalized)) return normalized;

    // For API media:
    // Always bind direct URL for <video> so the browser can stream via Range using cookies.
    if (this.isApiUrl(normalized)) {
      if (options.purpose === 'vtt') {
        const vttText = await this.fetchText(normalized);
        const blob = new Blob([vttText], { type: 'text/vtt' });
        return URL.createObjectURL(blob);
      }
      return normalized;
    }

    // Public/external URLs can be used directly.
    return normalized;
  }

  private async ensureVttBlobUrl(): Promise<void> {
    const video = this.video;
    if (!video) return;
    if (!this.isEmptyish(video?.vtt_text)) return; // already handled via vtt_text

    const raw = String((video as any).__vttApiUrl || video?.vttUrl || '').trim();
    if (this.isEmptyish(raw)) return;

    try {
      const objectUrl = await this.getPlayableUrlForProtectedMedia(raw, { purpose: 'vtt' });
      if (!objectUrl) return;

      if (this.resolvedVttObjectUrl && this.resolvedVttObjectUrl !== objectUrl) {
        try {
          URL.revokeObjectURL(this.resolvedVttObjectUrl);
        } catch {
          // ignore
        }
      }
      this.resolvedVttObjectUrl = objectUrl;
      video.vttBlobUrl = objectUrl;
    } catch {
      // fallback to URL (may be public)
      video.vttBlobUrl = this.buildMediaUrl(raw);
    }
  }

  private revokeResolvedObjectUrls(): void {
    // Only revoke the (potentially large) video blob URL.
    // Thumbnails/VTT blob URLs may be reused across component instances when filtering/searching,
    // and revoking them on destroy can cause thumbnails/captions to disappear.
    if (this.resolvedVideoObjectUrl) {
      try {
        URL.revokeObjectURL(this.resolvedVideoObjectUrl);
      } catch {
        // ignore
      }
    }
    this.resolvedVideoObjectUrl = undefined;

    // Keep these references cleared, but do not revoke.
    this.resolvedThumbnailObjectUrl = undefined;
    this.resolvedVttObjectUrl = undefined;

    // POST-generated object URLs may be large; clear cache.
    // We don't revoke here because URLs can be in use by <img>/<video>.
    this.postObjectUrlCache.clear();
    this.postObjectUrlInFlight.clear();
  }

   private getThumbnailCacheKey(video: any): string {
    const idPart = (video?.id ?? '').toString().trim();
    const blobPart = (video?.video_blobname ?? video?.videoBlobName ?? '').toString().trim();
    return `videoThumbnail:${blobPart || idPart || this.index}`;
  }

  private cacheThumbnailUrl(video: any, url: string): void {
    try {
      sessionStorage.setItem(this.getThumbnailCacheKey(video), url);
    } catch {
      // ignore
    }
  }

  private getCachedThumbnailUrl(video: any): string {
    try {
      return sessionStorage.getItem(this.getThumbnailCacheKey(video)) || '';
    } catch {
      return '';
    }
  }

  private isEmptyish(value: any): boolean {
    if (value === null || value === undefined) return true;
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (trimmed.length === 0) return true;
    const lower = trimmed.toLowerCase();
    return lower === 'null' || lower === 'undefined';
  }

  // Only treat known embed providers (YouTube/Vimeo/etc.) as iframe videos.
  // Regular MP4/blob/SAS URLs should use the native <video> element.
  isIframeVideo(): boolean {
    const link = (this.video?.videoUrl || '').toString();
    return !!link && /youtube|vimeo|embed/i.test(link);
  }

   /**
   * Returns the best playable URL for thumbnail generation / playback.
   * Some APIs populate `videoUrl` with a normal mp4/blob URL.
   */
  private getEffectiveVideoUrl(video: any): string {
    const direct = !this.isEmptyish(video?.videoUrl) ? String(video.videoUrl) : '';
    if (direct) return this.buildMediaUrl(direct);

    const iframeLike = !this.isEmptyish(video?.videoUrl) ? String(video.videoUrl) : '';
    if (!iframeLike) return '';

    // Don't treat real embed URLs as playable files.
    if (/youtube|vimeo|embed/i.test(iframeLike)) return '';

    return this.buildMediaUrl(iframeLike);
  }

  /** Called when an existing thumbnailUrl fails to load (e.g., blob was deleted). */
  onThumbnailError(video: any): void {
    if (!video) return;

    // Avoid infinite retry loops.
    if (video.__thumbnailImageLoadFailed) return;
    video.__thumbnailImageLoadFailed = true;

    const current = String(video.thumbnailUrl || '').trim();
    // If it's already a generated preview, don't retry here.
    if (/^(blob:|data:)/i.test(current)) return;

    // Clear and retry generation/upload.
    video.thumbnailUrl = '';
    // allow retry even if a previous attempt marked failed
    video.__thumbnailGenerationFailed = false;
    video.__thumbnailGenerationFailedAt = 0;
    this.ensureThumbnail();
    this.emitMediaReady();
  }

  private setGeneratedThumbnailObjectUrl(objectUrl: string): void {
    if (this.generatedThumbnailObjectUrl && this.generatedThumbnailObjectUrl !== objectUrl) {
      this.revokeGeneratedThumbnailObjectUrl();
    }
    this.generatedThumbnailObjectUrl = objectUrl;
  }

  private revokeGeneratedThumbnailObjectUrl(): void {
    if (!this.generatedThumbnailObjectUrl) return;
    // Only revoke if we're still pointing at the generated object URL.
    if (this.video?.thumbnailUrl === this.generatedThumbnailObjectUrl) {
      try {
        URL.revokeObjectURL(this.generatedThumbnailObjectUrl);
      } catch {
        // ignore
      }
    }
    this.generatedThumbnailObjectUrl = undefined;
  }



    generateVttBlobUrl(vttText: string): string {
    const blob = new Blob([vttText], { type: 'text/vtt' });
    return URL.createObjectURL(blob);
  }

  async playVideo(video: any) {
    // if (!video) {
    //   console.error('Invalid video object');
    //   return;
    // }

    // video.is_playing = true; // Add autoplay=1 to the iframe link

    // const separator = video.videoUrl.includes('?') ? '&' : '?';
    // video.autoplayLink = `${video.videoUrl}${separator}autoplay=1`;
    const sourceUrl = this.getEffectiveVideoUrl(video);
    if (!video || video.is_playing || this.isEmptyish(sourceUrl)) return;

    // Show thumbnail overlay until the first frame is ready.
    (video as any).__isVideoReady = false;

    // Resolve protected API video to a blob URL so we don't put auth tokens in query strings.
    let playableUrl = sourceUrl;
    try {
      playableUrl = await this.getPlayableUrlForProtectedMedia(sourceUrl, { purpose: 'video' });
      if (/^blob:/i.test(playableUrl)) {
        if (this.resolvedVideoObjectUrl && this.resolvedVideoObjectUrl !== playableUrl) {
          try {
            URL.revokeObjectURL(this.resolvedVideoObjectUrl);
          } catch {
            // ignore
          }
        }
        this.resolvedVideoObjectUrl = playableUrl;
      }
    } catch (e) {
      // If blob conversion fails for a protected API URL, do NOT fall back to direct <video src>.
      // The browser cannot attach Authorization headers to <video> tag requests, which would cause
      // an additional unauthenticated request (and often a failure) in parallel.
      const normalized = this.buildMediaUrl(sourceUrl);
      if (this.isApiUrl(normalized)) {
        console.warn('Video fetch (blob) failed; not falling back to direct src', e);
        return;
      }

      // For public/external URLs, falling back is fine.
      playableUrl = sourceUrl;
    }

    // Ensure template binding (<video [src]="video.videoUrl">) has a value.
    video.videoUrl = playableUrl;

    // IMPORTANT: only now render the <video> element.
    // If we set is_playing earlier, Angular would render <video [src]="video.videoUrl"> with a protected API URL
    // and the browser would auto-request it without Authorization header.
    video.is_playing = true;

    setTimeout(() => {
      const videoEl = document.getElementById(`video-${this.index}`) as HTMLVideoElement | null;
      if (videoEl) {
        const playPromise = videoEl.play();
        if (playPromise && typeof (playPromise as any).catch === 'function') {
          (playPromise as any).catch(() => {});
        }
      }

      // Wire custom controls after the native <video> exists in DOM.
      this.attemptWireNativePlayer();
    }, 0);
  }

  onVideoReady(video: any): void {
    if (!video) return;
    (video as any).__isVideoReady = true;
  }

  onVideoWaiting(video: any): void {
    if (!video) return;
    (video as any).__isVideoReady = false;
  }

  onThumbnailLoad(): void {
    const thumbnailSrc = String(this.video?.thumbnailUrl || '').trim();
    if (!thumbnailSrc || thumbnailSrc === this.transparentPixel) {
      return;
    }

    this.emitMediaReady();
  }

  onIframeLoad(): void {
    this.emitMediaReady();
  }

  private emitMediaReady(): void {
    if (this.hasEmittedMediaReady) {
      return;
    }

    this.hasEmittedMediaReady = true;
    this.mediaReady.emit();
  }


  private coerceVideoFlags(video: any): void {
    if (!video) return;
    video.is_playing = this.toBoolean(video.is_playing);
  }

  private toBoolean(value: any): boolean {
    if (typeof value === 'boolean') return value;
    if (typeof value === 'number') return value !== 0;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      if (v === 'true') return true;
      if (v === 'false') return false;
      if (v === '1') return true;
      if (v === '0') return false;
      // Non-empty string is truthy in JS, but for flags we default to false.
      return false;
    }
    return false;
  }

 

  // helper to determine whether the provided link is an embed/iframe (YouTube/Vimeo/etc.)
  // isIframeVideo(): boolean {
  //   return !!this.video?.videoUrl && /youtube|vimeo|embed/i.test(this.video.videoUrl);
  // }

  onHover(state: boolean): void {
    this.hovered = state;
  }

  
toggleFullscreen(index: number): void {
  const player = document.getElementById(`player-${index}`);
  if (!player) return;

  const isFullscreen =
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).msFullscreenElement;

  if (isFullscreen) {
    document.exitFullscreen?.() || (document as any).webkitExitFullscreen?.();
  } else {
    player.requestFullscreen?.() || (player as any).webkitRequestFullscreen?.();
  }
}

onFullscreenChange = (): void => {
  const isFullscreen =
    document.fullscreenElement ||
    (document as any).webkitFullscreenElement ||
    (document as any).msFullscreenElement;

  document.body.classList.toggle('player-fullscreen', !!isFullscreen);
};



   ngAfterViewInit(): void {
    // On initial load the native <video> may not exist (it renders only when playing).
    // Don't warn; just try wiring if/when it becomes available.
    this.attemptWireNativePlayer();
  }
  
  private attemptWireNativePlayer(attempt = 0): void {
    if (this.nativePlayerWired) return;

    // If this is an embedded iframe (YouTube/Vimeo), we cannot control playback from here.
    if (this.isIframeVideo()) {
      document.getElementById(`controls-${this.index}`)?.classList.add('hidden');
      return;
    }

    const video = document.getElementById(`video-${this.index}`) as HTMLVideoElement | null;
    const controls = document.getElementById(`controls-${this.index}`);
    const player = document.getElementById(`player-${this.index}`);

    // When not playing yet, the <video> and control DOM nodes aren't rendered.
    if (!video || !controls || !player) {
      // Only retry if user already clicked play.
      if (this.video?.is_playing && attempt < 20) {
        setTimeout(() => this.attemptWireNativePlayer(attempt + 1), 50);
      }
      return;
    }

    this.nativePlayerWired = true;

    // From here on, these are non-null.
    const videoEl = video;
    const controlsEl = controls;
    const playerEl = player;

    const volumeHighSVG = '<img class="stkh-table-filter-img" src="../assets/images/videooptions/speakers.png">';
    const volumeLowSVG = '<img class="stkh-table-filter-img" src="../assets/images/videooptions/lowsound.png">';
    const volumeMuteSVG = '<img class="stkh-table-filter-img" src="../assets/images/videooptions/mute.png">';

    videoEl.addEventListener('dblclick', (e) => {
      e.preventDefault();
      this.toggleFullscreen(this.index);
    });

    
    let hideTimeout: any;

    function showControls(): void {
      controlsEl.classList.remove('hidden');
      clearTimeout(hideTimeout);
    }

    function hideControls(): void {
      if (!videoEl.paused && !playerEl.matches(':hover')) {
        hideTimeout = setTimeout(() => {
          if (!playerEl.matches(':hover')) {
            controlsEl.classList.add('hidden');
          }
        }, 2000); // Delay hiding by 2 seconds
      }
    }

    playerEl.addEventListener('mouseenter', () => {
      showControls();
      clearTimeout(hideTimeout); // Ensure controls remain visible while hovering
    });

    playerEl.addEventListener('mouseleave', hideControls);

    videoEl.addEventListener('play', showControls);
    videoEl.addEventListener('pause', showControls);

    // Ensure controls are hidden initially if video is not paused
    if (!videoEl.paused) {
      hideControls();
    }

    const playPauseBtn = document.getElementById(`playPause-${this.index}`);
    const centerPlayBtn = document.getElementById(`centerPlayBtn-${this.index}`);
    const rewindBtn = document.getElementById(`rewind-${this.index}`);
    const forwardBtn = document.getElementById(`forward-${this.index}`);
    const fullscreenBtn = document.getElementById(`fullscreen-${this.index}`);
    const timeDisplay = document.getElementById(`time-${this.index}`);
    const volumeBtn = document.getElementById(`volumeBtn-${this.index}`);
    const volumePanel = document.getElementById(`volumePanel-${this.index}`);
    const volumeRange = document.getElementById(`volumeRange-${this.index}`) as HTMLInputElement | null;
    const captionsBtn = document.getElementById(`captions-${this.index}`);
    const speedPanel = document.getElementById(`speedPanel-${this.index}`);
    const speedBtn = document.getElementById(`speedBtn-${this.index}`);
    const centerOverlay = document.getElementById(`centerOverlay-${this.index}`);

    const volUpBtn = document.getElementById('volUp');
    const volDownBtn = document.getElementById('volDown');
    const muteBtn = document.getElementById('mute');

    const progressContainer = document.getElementById(`progressContainer-${this.index}`);
    const progress = document.getElementById(`progress-${this.index}`);
    // const track = document.getElementById('englishCaption') as HTMLTrackElement | null;
    const playPauseImg = document.getElementById(`playPauseImg-${this.index}`) as HTMLImageElement;

    // video/controls/player are guaranteed by the checks above

    // sensible default volume for native videos
    if (typeof videoEl.volume === 'undefined' || videoEl.volume === 0) videoEl.volume = 0.5;

    function formatTime(sec: number): string {
      if (!isFinite(sec)) return '0:00';
      const m = Math.floor(sec / 60) || 0;
      const s = Math.floor(sec % 60) || 0;
      return `${m}:${s < 10 ? '0' : ''}${s}`;
    }

    function updateTime(): void {
      if (!timeDisplay) return;
      timeDisplay.textContent = `${formatTime(videoEl.currentTime)} / ${formatTime(videoEl.duration)}`;
    }

    function updateProgress(): void {
      if (!progress) return;
      const pct = (videoEl.currentTime / (videoEl.duration || 1)) * 100;
      (progress as HTMLElement).style.width = `${pct}%`;
    }

    function setPlaying(isPlaying: boolean): void {
      
      if (playPauseImg) {
        playPauseImg.src = isPlaying
          ? '../assets/images/videooptions/pause.png'
          : '../assets/images/videooptions/play.png';
      }

      // if (playPauseBtn) playPauseBtn.textContent = isPlaying ? '⏸' : '▶';
      if (centerOverlay) {

        centerOverlay.classList.toggle('hidden', isPlaying);
      }
      // if (controls) controls.classList.toggle('hidden', isPlaying);
    }

    function togglePlay(): void {
      if (videoEl.paused) {
        const playPromise = videoEl.play();
        if (playPromise && typeof playPromise.then === 'function') playPromise.catch(() => {});
      } else {
        videoEl.pause();
      }
    }


    videoEl.addEventListener('play', () => setPlaying(true));
    videoEl.addEventListener('pause', () => setPlaying(false));
    if (centerPlayBtn)
      centerPlayBtn.addEventListener('click', e => {
        e.stopPropagation();
        togglePlay();
        showControls();
      });
    videoEl.addEventListener('click', togglePlay);
    if (playPauseBtn) playPauseBtn.addEventListener('click', togglePlay);

    if (rewindBtn)
      rewindBtn.addEventListener('click', () => {
        videoEl.currentTime = Math.max(0, videoEl.currentTime - 10);
      });
    if (forwardBtn)
      forwardBtn.addEventListener('click', () => {
        videoEl.currentTime = Math.min(videoEl.duration || videoEl.currentTime + 10, videoEl.currentTime + 10);
      });


      
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange as any);
    document.addEventListener('msfullscreenchange', this.onFullscreenChange as any);


    function updateMuteIcon(): void {
      if (!muteBtn) return;
      const icon = videoEl.muted || videoEl.volume === 0 ? '🔇' : '🔊';
      muteBtn.textContent = icon;
      muteBtn.setAttribute('aria-label', videoEl.muted ? 'Unmute' : 'Mute');
    }

    if (volUpBtn)
      volUpBtn.addEventListener('click', () => {
        videoEl.volume = Math.min(1, (videoEl.volume || 0) + 0.1);
        updateMuteIcon();
      });
    if (volDownBtn)
      volDownBtn.addEventListener('click', () => {
        videoEl.volume = Math.max(0, (videoEl.volume || 0) - 0.1);
        updateMuteIcon();
      });
    if (muteBtn)
      muteBtn.addEventListener('click', () => {
        videoEl.muted = !videoEl.muted;
        updateMuteIcon();
      });
    videoEl.addEventListener('volumechange', updateMuteIcon);

    videoEl.addEventListener('timeupdate', () => {
      updateProgress();
      updateTime();
    });
    videoEl.addEventListener('loadedmetadata', () => {
      updateTime();
      updateProgress();
    });
    if (progressContainer)
      progressContainer.addEventListener('click', e => {
        const rect = (progressContainer as HTMLElement).getBoundingClientRect();
        const ratio = (e as MouseEvent).clientX - rect.left;
        const pct = ratio / rect.width;
        videoEl.currentTime = pct * (videoEl.duration || 0);
      });


    // default unmuted volume
    videoEl.muted = false;
    videoEl.volume = videoEl.volume || 0.5;

    function updateVolumeIcon(): void {
      // const volumeBtn = document.getElementById(`volumeBtn-${this.index}`);
      if (!volumeBtn) return;

      if (videoEl.muted || videoEl.volume === 0) {
        volumeBtn.innerHTML = volumeMuteSVG;
      } else if (videoEl.volume < 0.5) {
        volumeBtn.innerHTML = volumeLowSVG;
      } else {
        volumeBtn.innerHTML = volumeHighSVG;
      }
    }

    if (volumeBtn) {
      volumeBtn.addEventListener('click', e => {
        e.stopPropagation();
        if (!volumePanel) return;
        volumePanel.classList.toggle('hidden');
        const ariaHidden = volumePanel.classList.contains('hidden') ? 'true' : 'false';
        volumePanel.setAttribute('aria-hidden', ariaHidden);
      });
    }

    if (volumeRange) {
      // init range value
      volumeRange.value = String(videoEl.volume ?? 0.5);
      volumeRange.addEventListener('input', e => {
        const val = parseFloat((e.target as HTMLInputElement).value);
        if (isNaN(val)) return;
        videoEl.volume = val;
        videoEl.muted = val === 0;
        updateVolumeIcon();
      });

      // close panel when clicking outside
      document.addEventListener('click', e => {
        if (!volumePanel || !volumeBtn) return;
        if (!(volumePanel.contains(e.target as Node) || volumeBtn.contains(e.target as Node))) {
          volumePanel.classList.add('hidden');
        }
      });
    }

    // ensure icon updates when mute/volume change programmatically
    videoEl.addEventListener('volumechange', updateVolumeIcon);

    // initial icon
    updateVolumeIcon();
    setPlaying(false);

    if (captionsBtn) {
      const textTracks = videoEl.textTracks;

      // Set initial state (optional)
      if (textTracks.length > 0) {
        textTracks[0].mode = 'hidden'; // Start with captions hidden
      }

      captionsBtn.addEventListener('click', () => {
        if (textTracks.length > 0) {
          const track = textTracks[0];
          const isShowing = track.mode === 'showing';

          // Toggle caption visibility
          track.mode = isShowing ? 'hidden' : 'showing';

          // Toggle button active state
          captionsBtn.classList.toggle('active', !isShowing);
        }
      });
    }

    if (speedPanel) {
      // toggle panel when clicking the speed button
      if (speedBtn) {
        speedBtn.addEventListener('click', e => {
          e.stopPropagation();
          speedPanel.classList.toggle('hidden');
          speedPanel.setAttribute('aria-hidden', speedPanel.classList.contains('hidden') ? 'true' : 'false');
        });
      }

      speedPanel.querySelectorAll('.speed-option').forEach(opt => {
        opt.addEventListener('click', ev => {
          const val = (ev.currentTarget as HTMLElement).getAttribute('data-speed');
          const num = parseFloat(val || '1');
          videoEl.playbackRate = num;
          if (speedBtn) speedBtn.textContent = `${num}x`;
          // close panel
          speedPanel.classList.add('hidden');
          speedPanel.setAttribute('aria-hidden', 'true');
        });
      });

      // close panel when clicking outside
      document.addEventListener('click', e => {
        if (!speedPanel || !speedBtn) return;
        if (!(speedPanel.contains(e.target as Node) || speedBtn.contains(e.target as Node))) {
          speedPanel.classList.add('hidden');
        }
      });
    }
  }
}
